import { Box } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import {
	DataGridPro,
	type GridColDef,
	type GridFilterModel,
	type GridPaginationModel,
	type GridRowSelectionModel,
	type GridSortModel,
	useGridApiRef,
} from '@mui/x-data-grid-pro'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AdditionalFiltersToolbar } from './AdditionalFiltersToolbar'
import { GridApi } from './api/gridApi'
import { buildColumns } from './buildColumns'
import { GridPagination } from './GridPagination'
import { useGridTheme } from './gridTheme'
import { GroupActionsBar } from './GroupActionsBar'
import { RowActionsCell } from './RowActionsCell'
import { Toolbar } from './Toolbar'
import type { GridColumnFilterValue, GridConfig, GridEndpoints, GridFilterDefinition, GridRow } from './types'
import { useGridData } from './useGridData'
import { isNewRowId, useGridEditing } from './useGridEditing'

type Props = {
	config: GridConfig
	endpoints: GridEndpoints
}

const ROW_ACTIONS_FIELD = '__rowActions'
const DEFAULT_HEIGHT = 480
const MIN_HEIGHT = 240

// Match the parent `.js-resize-item-block` shell so the grid fills the block
// content area minus its header/footer (same shape the legacy VerbatimsGrid used).
// Falls back to DEFAULT_HEIGHT when not nested inside a resizable block.
const computeGridHeight = (containerEl: HTMLElement): number => {
	const block = containerEl.closest('.js-resize-item-block') as HTMLElement | null
	if (block === null) {
		return DEFAULT_HEIGHT
	}
	const header = block.querySelector<HTMLElement>('.js-resize-item-head')
	const footer = block.querySelector<HTMLElement>('.js-resize-item-footer')
	const minus = block.querySelectorAll<HTMLElement>('.js-resize-height-minus')
	const headerH = header?.offsetHeight ?? 0
	const footerH = footer?.offsetHeight ?? 0
	let minusH = 0
	minus.forEach((el) => {
		minusH += el.offsetHeight
	})
	return Math.max(block.offsetHeight - headerH - footerH - minusH, MIN_HEIGHT)
}

export const ReactGrid: React.FC<Props> = ({ config, endpoints }) => {
	const api = useMemo(() => new GridApi(endpoints), [endpoints])
	const gridApiRef = useGridApiRef()
	const wrapperRef = useRef<HTMLDivElement | null>(null)
	const [gridHeight, setGridHeight] = useState<number>(DEFAULT_HEIGHT)

	useEffect(() => {
		if (wrapperRef.current === null) {
			return undefined
		}
		const apply = (): void => {
			if (wrapperRef.current !== null) {
				setGridHeight(computeGridHeight(wrapperRef.current))
			}
		}
		apply()
		window.addEventListener('resize', apply)
		return () => {
			window.removeEventListener('resize', apply)
		}
	}, [])

	const {
		rows,
		total,
		loading,
		pagination,
		sort,
		columnFilters,
		additionalFilters,
		selection,
		setPagination,
		setSort,
		setColumnFilters,
		setAdditionalFilters,
		setSelection,
		refresh,
	} = useGridData(config, api)

	const [localExtras, setLocalExtras] = useState<GridRow[]>([])

	const handleFlash = useCallback((message: string, _type: string | null) => {
		window.dispatchEvent(new CustomEvent('elis:flash', { detail: { message } }))
	}, [])

	const editing = useGridEditing({
		config,
		api,
		rows: localExtras,
		setRows: setLocalExtras,
		onFlash: handleFlash,
	})

	const wrappedProcessRowUpdate = useCallback(
		async (
			newRow: Parameters<typeof editing.processRowUpdate>[0],
			oldRow: Parameters<typeof editing.processRowUpdate>[1],
		) => {
			const result = await editing.processRowUpdate(newRow, oldRow)
			const rowId = (newRow as Record<string, unknown>)[config.rowIdField]
			if (typeof rowId === 'string' && isNewRowId(rowId)) {
				setLocalExtras((prev) => prev.filter((r) => r[config.rowIdField] !== rowId))
				refresh()
			}
			return result
		},
		[editing, config.rowIdField, refresh],
	)

	const handleSignalAction = useCallback(
		(actionKey: string, rowId: string | number) => {
			void api.action(actionKey, rowId).then((res) => {
				if (res.reload) {
					refresh()
				}
			})
		},
		[api, refresh],
	)

	const handleGroupAction = useCallback(
		(actionKey: string) => {
			void api.groupAction(actionKey, selection).then((res) => {
				setSelection([])
				if (res.reload) {
					refresh()
				}
			})
		},
		[api, refresh, selection, setSelection],
	)

	const handleExport = useCallback(() => {
		const form = document.createElement('form')
		form.method = 'POST'
		form.action = endpoints.export
		form.style.display = 'none'
		const input = document.createElement('input')
		input.name = 'payload'
		input.value = JSON.stringify({ sort, columnFilters, additionalFilters })
		form.appendChild(input)
		document.body.appendChild(form)
		form.submit()
		document.body.removeChild(form)
	}, [endpoints.export, sort, columnFilters, additionalFilters])

	const allRows = useMemo(() => [...localExtras, ...rows], [localExtras, rows])

	// Keep the latest filter list behind a ref so the column definitions can read
	// the current value without re-running the columns useMemo on every keystroke.
	// Rebuilding columns would hand MUI a fresh column array, which re-mounts the
	// header filter cell mid-keystroke and yanks focus away from the input.
	const columnFiltersRef = useRef<GridColumnFilterValue[]>(columnFilters)
	columnFiltersRef.current = columnFilters

	const handleColumnFilterChange = useCallback(
		(field: string, kind: GridFilterDefinition['kind'], value: unknown) => {
			const isEmpty =
				value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)
			const without = columnFiltersRef.current.filter((f) => f.field !== field)
			setColumnFilters(isEmpty ? without : [...without, { field, kind, value }])
		},
		[setColumnFilters],
	)

	const hasColumnFilter = useMemo(() => config.columns.some((c) => c.filter !== null), [config.columns])

	const columns = useMemo<GridColDef[]>(() => {
		const base = buildColumns({
			gridId: config.id,
			columns: config.columns,
			getFilterValue: (field) => columnFiltersRef.current.find((f) => f.field === field)?.value,
			onColumnFilterChange: handleColumnFilterChange,
			inlineEdit: config.inlineEdit,
		})

		const inlineEditEnabled = config.inlineEdit?.enabled === true
		if (!inlineEditEnabled && config.columnActions.length === 0) {
			return base
		}

		const viewIcons = (inlineEditEnabled ? 1 : 0) + config.columnActions.length
		const editIcons = 2 // save + cancel
		const widthInIcons = Math.max(viewIcons, editIcons)

		const actionsCol: GridColDef = {
			field: ROW_ACTIONS_FIELD,
			headerName: '',
			sortable: false,
			filterable: false,
			hideable: false,
			width: 40 + widthInIcons * 40,
			renderCell: (params) => (
				<RowActionsCell
					rowId={params.id}
					row={params.row as GridRow}
					rowIdField={config.rowIdField}
					rowModesModel={editing.rowModesModel}
					inlineEditEnabled={inlineEditEnabled}
					columnActions={config.columnActions}
					onEdit={editing.startEdit}
					onSave={editing.commitEdit}
					onCancel={editing.cancelEdit}
					onSignal={handleSignalAction}
				/>
			),
		}

		return [...base, actionsCol]
	}, [
		config.columnActions,
		config.columns,
		config.id,
		config.inlineEdit,
		config.rowIdField,
		editing.rowModesModel,
		editing.startEdit,
		editing.commitEdit,
		editing.cancelEdit,
		handleSignalAction,
		handleColumnFilterChange,
	])

	const paginationModel = useMemo<GridPaginationModel>(
		() => ({ page: pagination.page, pageSize: pagination.pageSize }),
		[pagination],
	)

	const sortModel = useMemo<GridSortModel>(() => sort.map((s) => ({ field: s.field, sort: s.direction })), [sort])

	const filterModel = useMemo<GridFilterModel>(
		() => ({
			items: columnFilters.map((f, i) => ({
				id: i,
				field: f.field,
				operator: 'contains',
				value: f.value,
			})),
		}),
		[columnFilters],
	)

	const theme = useGridTheme()

	return (
		<ThemeProvider theme={theme}>
			<Box ref={wrapperRef} sx={{ width: '100%', height: gridHeight, display: 'flex', flexDirection: 'column' }}>
				<AdditionalFiltersToolbar
					filters={config.additionalFilters}
					values={additionalFilters}
					onChange={setAdditionalFilters}
				/>
				<GroupActionsBar actions={config.groupActions} selectedCount={selection.length} onAction={handleGroupAction} />
				<Box sx={{ flexGrow: 1, minHeight: 0 }}>
					<DataGridPro
						apiRef={gridApiRef}
						rows={allRows}
						columns={columns}
						rowCount={total}
						loading={loading}
						pagination
						paginationMode="server"
						sortingMode="server"
						filterMode="server"
						headerFilters={hasColumnFilter}
						disableColumnFilter
						editMode="row"
						rowModesModel={editing.rowModesModel}
						onRowModesModelChange={editing.setRowModesModel}
						processRowUpdate={wrappedProcessRowUpdate}
						onProcessRowUpdateError={(err) => {
							const message =
								typeof (err as { flash?: string })?.flash === 'string'
									? (err as { flash: string }).flash
									: 'Save failed'
							window.dispatchEvent(new CustomEvent('elis:flash', { detail: { message } }))
						}}
						paginationModel={paginationModel}
						onPaginationModelChange={(m) => setPagination({ page: m.page, pageSize: m.pageSize })}
						sortModel={sortModel}
						onSortModelChange={(model) =>
							setSort(model.map((m) => ({ field: m.field, direction: m.sort === 'desc' ? 'desc' : 'asc' })))
						}
						filterModel={filterModel}
						onFilterModelChange={(model) => {
							const next = model.items
								.filter((i) => i.value !== undefined && i.value !== null && i.value !== '')
								.map((i) => {
									const colDef = config.columns.find((c) => c.field === i.field)
									return {
										field: i.field,
										kind: colDef?.filter?.kind ?? 'text',
										value: i.value as unknown,
									}
								})
							setColumnFilters(next)
						}}
						checkboxSelection={config.selectionMode === 'multiple'}
						disableRowSelectionOnClick
						rowSelectionModel={
							{
								type: config.selectionMode === 'multiple' ? 'include' : 'include',
								ids: new Set(selection),
							} as GridRowSelectionModel
						}
						onRowSelectionModelChange={(model: GridRowSelectionModel) => {
							const ids = Array.from(model.ids ?? new Set()) as ReadonlyArray<string | number>
							setSelection(ids)
						}}
						pageSizeOptions={config.pagination.pageSizeOptions}
						rowHeight={56}
						columnHeaderHeight={48}
						getRowId={(row: GridRow) => {
							const id = row[config.rowIdField]
							return typeof id === 'string' || typeof id === 'number' ? id : Math.random()
						}}
						showToolbar={config.exportable || config.inlineEdit?.allowAdd === true}
						slots={{
							toolbar: () => (
								<Toolbar
									onExport={config.exportable ? handleExport : undefined}
									onAdd={config.inlineEdit?.allowAdd === true ? editing.startAdd : undefined}
								/>
							),
							pagination: () => <GridPagination pageSizeOptions={config.pagination.pageSizeOptions} />,
						}}
						sx={{
							height: '100%',
							width: '100%',
							'& .MuiDataGrid-toolbarContainer': {
								borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
							},
							'& .MuiDataGrid-headerFilterRow': {
								backgroundColor: 'rgba(0, 0, 0, 0.02)',
								'& .MuiDataGrid-columnHeader': {
									pt: 1,
									pb: 1,
									display: 'flex',
									alignItems: 'center',
									minHeight: '64px !important',
									maxHeight: 'none !important',
									height: 'auto !important',
									'&:focus': {
										outline: 'none',
									},
									'&:focus-within': {
										outline: 'none',
									},
								},
							},
							'& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell--editing': {
								outline: 'none !important',
							},
							'& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
								outline: 'none',
							},
							// Row click should never look "selected" — only the checkbox controls selection.
							'& .MuiDataGrid-row.Mui-selected': {
								backgroundColor: 'transparent',
								'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
							},
							'& .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within': {
								outline: 'none',
							},
						}}
					/>
				</Box>
			</Box>
		</ThemeProvider>
	)
}
